import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import { setAuth } from '../utils/auth';
import { trackLogin } from '../utils/clickTracking';
import { FiUser, FiLock } from 'react-icons/fi';

const LoginPage = () => {
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post('/auth/login', form);
      const { token, user, message } = res.data;
      setAuth(token, user);
      if (user?.id) {
        await trackLogin(user.id, user.role);
      }
      toast.success(message || 'Login successful');
      navigate(`/${user.role}/`);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed. Please try again.';
      toast.error(errorMessage);
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-green-100 via-white to-emerald-200 px-4">
      <div className="relative w-full max-w-md bg-white/70 backdrop-blur-xl border border-gray-200/60 shadow-2xl rounded-3xl p-10 transition-all duration-300 hover:shadow-green-200/80">
        
        {/* Decorative Glow */}
        <div className="absolute -top-6 -right-6 w-24 h-24 bg-green-400 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-emerald-500 rounded-full blur-3xl opacity-20"></div>

        {/* Logo / Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-600 to-emerald-500 text-white flex items-center justify-center text-2xl font-bold shadow-lg shadow-green-500/30">
              CV
            </div>
          </div>
          <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight">
            Circle Dashboard
          </h1>
          <p className="text-gray-500 text-sm mt-1">Authorized Persons Login Only</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Username */}
          <div>
            <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-1">
              Username
            </label>
            <div className="flex items-center border border-gray-300 rounded-xl bg-white/90 overflow-hidden shadow-sm focus-within:ring-2 focus-within:ring-emerald-400">
              <span className="pl-3 pr-2 text-gray-500">
                <FiUser />
              </span>
              <input
                type="text"
                name="username"
                id="username"
                autoComplete="username"
                value={form.username}
                onChange={handleChange}
                placeholder="Enter your username"
                required
                className="w-full px-3 py-2 focus:outline-none bg-transparent"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1">
              Password
            </label>
            <div className="flex items-center border border-gray-300 rounded-xl bg-white/90 overflow-hidden shadow-sm focus-within:ring-2 focus-within:ring-emerald-400">
              <span className="pl-3 pr-2 text-gray-500">
                <FiLock />
              </span>
              <input
                type="password"
                name="password"
                id="password"
                autoComplete="current-password"
                value={form.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
                className="w-full px-3 py-2 focus:outline-none bg-transparent"
              />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-xl font-semibold shadow-lg text-white transition-all duration-300 transform ${
              loading
                ? 'bg-emerald-400 cursor-not-allowed opacity-60'
                : 'bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 hover:scale-[1.02]'
            }`}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {/* Help Link */}
        <div className="mt-6 text-center text-sm text-gray-500">
          Need access?{' '}
          <button
            onClick={() => setShowEmail(!showEmail)}
            className="text-emerald-600 font-medium hover:underline focus:outline-none"
          >
            Contact Admin
          </button>

          {/* Email text that appears when button is clicked */}
          {showEmail && (
            <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200 inline-block">
              <span className="text-emerald-700 font-medium">0341-0925767</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
